import React, { useState, useEffect } from 'react';
import { getManualSales } from '../../api';

const ManualSales = ({ menuItems, sales, onUpdateSales, recipes }) => {

  const [currentSales, setCurrentSales] = useState([]);

  // 🔹 Cargar ventas manuales desde backend al iniciar
  useEffect(() => {

    const loadManualSales = async () => {

      try {

        const response = await getManualSales();

        if (!response || !response.success) {
          console.error('Error cargando ventas manuales');
          return;
        }

        const manualSales = response.data.map(sale => ({
          id: Number(sale.id),
          quantitySold: Number(sale.quantitySold) || 0,
          type: 'manual',
          source: 'manual'
        }));

        // 🔥 sincronizar currentSales con backend
        setCurrentSales(prev =>
          prev.map(item => {

            const found = manualSales.find(ms => Number(ms.id) === Number(item.id));

            if (found) {

              return {
                ...item,
                quantitySold: Number(found.quantitySold) || 0
              };

            }

            return item;

          })
        );

        if (typeof onUpdateSales === 'function') {

          const updatedSales = Array.isArray(sales) ? [...sales] : [];

          manualSales.forEach(ms => {

            const index = updatedSales.findIndex(
              s => Number(s.id) === Number(ms.id) && s.type === 'manual'
            );

            if (index > -1) {

              updatedSales[index] = ms;

            } else {

              updatedSales.push(ms);

            }

          });

          onUpdateSales(updatedSales);

        }

      } catch (error) {

        console.error('Error cargando ventas manuales:', error);

      }

    };

    loadManualSales();

  }, [sales, onUpdateSales]);


  // 🔹 Inicializar currentSales con cantidades existentes
  useEffect(() => {

    if (Array.isArray(menuItems)) {

      setCurrentSales(menuItems.map(item => {

        const existingSale = Array.isArray(sales)
          ? sales.find(sale =>
              Number(sale.id) === Number(item.id) &&
              sale.type === 'manual'
            )
          : undefined;

        return {
          id: item.id,
          name: item.name,
          price: Number(item.price) || 0,
          priceWithoutVAT: Number(item.priceWithoutVAT) || 0,
          quantitySold: existingSale ? Number(existingSale.quantitySold) : 0,
          type: 'manual',
          source: 'manual'
        };

      }));

    } else {

      setCurrentSales([]);

    }

  }, [menuItems, sales]);


  // 🔹 Cambiar cantidad de un producto
  const handleQuantityChange = (id, value) => {

    const quantity = Number(value) || 0;

    setCurrentSales(prevSales =>
      prevSales.map(sale =>
        sale.id === id ? { ...sale, quantitySold: quantity } : sale
      )
    );

  };


  // 🔹 Guardar venta manual y enviar al backend
  const handleSaveSale = async (id) => {

    const saleToSave = currentSales.find(sale => sale.id === id);

    if (!saleToSave) return;

    try {

      const response = await fetch('/api/create_or_update_manual_sale.php', {

        method: 'POST',

        headers: {
          'Content-Type': 'application/json'
        },

        body: JSON.stringify(saleToSave)

      });

      const result = await response.json();

      if (!result.success) {

        console.error('Error guardando la venta en backend:', result.error);
        return;

      }

      if (typeof onUpdateSales === 'function') {

        let updatedSales = Array.isArray(sales) ? [...sales] : [];

        const existingIndex = updatedSales.findIndex(
          sale =>
            Number(sale.id) === Number(id) &&
            sale.type === 'manual'
        );

        if (existingIndex > -1) {

          updatedSales[existingIndex] = { ...saleToSave };

        } else if (saleToSave.quantitySold > 0) {

          updatedSales.push({ ...saleToSave });

        }

        updatedSales = updatedSales.filter(
          sale => sale.type !== 'manual' || sale.quantitySold > 0
        );

        onUpdateSales(updatedSales);

      } else {

        console.error('onUpdateSales no es una función');

      }

    } catch (error) {

      console.error('Error guardando la venta manual:', error);

    }

  };


  // 🔹 Limpiar cantidad
  const handleClearSale = async (id) => {

    try {

      const response = await fetch('/api/delete_manual_sale.php', {

        method: 'POST',

        headers: {
          'Content-Type': 'application/json'
        },

        body: JSON.stringify({ id })

      });

      const result = await response.json();

      if (!result.success) {

        console.error('Error limpiando venta manual:', result.error);
        return;

      }

      setCurrentSales(prevSales =>
        prevSales.map(sale =>
          sale.id === id ? { ...sale, quantitySold: 0 } : sale
        )
      );

      if (typeof onUpdateSales === 'function') {

        let updatedSales = Array.isArray(sales) ? [...sales] : [];

        updatedSales = updatedSales.filter(
          sale => !(Number(sale.id) === Number(id) && sale.type === 'manual')
        );

        onUpdateSales(updatedSales);

      }

    } catch (error) {

      console.error('Error limpiando venta manual:', error);

    }

  };


  // 🔹 Totales
  const totalWithoutVAT = currentSales.reduce(
    (total, sale) => total + ((Number(sale.priceWithoutVAT) || 0) * (Number(sale.quantitySold) || 0)),
    0
  );

  const totalWithVAT = currentSales.reduce(
    (total, sale) => total + ((Number(sale.price) || 0) * (Number(sale.quantitySold) || 0)),
    0
  );


  return (

    <div className="bg-[#F9EFD7] p-4 rounded-lg shadow">

      <h3 className="text-lg font-semibold text-[#3F1907] mb-3">
        Ingreso Manual de Ventas
      </h3>

      <div className="overflow-x-auto">

        <table className="min-w-full divide-y divide-[#58735A]">

          <thead className="bg-[#58735A]">

            <tr>

              <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase tracking-wider">
                Producto
              </th>

              <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase tracking-wider">
                Precio (con IVA)
              </th>

              <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase tracking-wider">
                Precio (sin IVA)
              </th>

              <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase tracking-wider">
                Cantidad Vendida
              </th>

              <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase tracking-wider">
                Acciones
              </th>

            </tr>

          </thead>

          <tbody className="bg-[#F9EFD7] divide-y divide-[#58735A]">

            {currentSales.map(sale => (

              <tr key={sale.id}>

                <td className="px-4 py-3 whitespace-nowrap text-sm text-[#3F1907]">
                  {sale.name}
                </td>

                <td className="px-4 py-3 whitespace-nowrap text-sm text-[#3F1907]">
                  ${Number(sale.price).toFixed(2)}
                </td>

                <td className="px-4 py-3 whitespace-nowrap text-sm text-[#3F1907]">
                  ${Number(sale.priceWithoutVAT).toFixed(2)}
                </td>

                <td className="px-4 py-3 whitespace-nowrap text-sm text-[#3F1907]">

                  <input
                    type="number"
                    min="0"
                    value={sale.quantitySold}
                    onChange={e => handleQuantityChange(sale.id, e.target.value)}
                    className="w-20 px-2 py-1 border border-[#58735A] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F1907] bg-[#F9EFD7] text-[#3F1907]"
                  />

                </td>

                <td className="px-4 py-3 whitespace-nowrap text-sm text-[#3F1907] space-x-2">

                  <button
                    onClick={() => handleSaveSale(sale.id)}
                    className="text-green-600 hover:text-green-800 transition duration-200"
                  >
                    Guardar
                  </button>

                  <button
                    onClick={() => handleClearSale(sale.id)}
                    className="text-gray-500 hover:text-gray-700 transition duration-200"
                  >
                    Limpiar
                  </button>

                </td>

              </tr>

            ))}

          </tbody>

          <tfoot>

            <tr>

              <td colSpan="3" className="px-4 py-3 text-right text-sm font-medium text-[#3F1907]">
                Total General (sin IVA):
              </td>

              <td></td>

              <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-[#3F1907]">
                ${totalWithoutVAT.toFixed(2)}
              </td>

            </tr>

            <tr>

              <td colSpan="3" className="px-4 py-3 text-right text-sm font-medium text-[#3F1907]">
                Total General (con IVA):
              </td>

              <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-[#3F1907]">
                ${totalWithVAT.toFixed(2)}
              </td>

              <td></td>

            </tr>

          </tfoot>

        </table>

      </div>

    </div>

  );

};

export default ManualSales;